import LoginPage from './LoginPage';
import useLoginMsal from './useLoginMsal';
import LoginMSALInitError from './LoginMSALInitError';
import { lazy, Suspense, useEffect } from 'react';
import LoginOTPRequestPage from './LoginOTPRequestPage';
import LoginBlankLoaderPage from './LoginBlankLoaderPage';
import LoginChoosePdvPage from './LoginChoosePDVPage';
import LoginChooseSessionPage from './LoginChooseSessionPage';
import { faLoveseat } from '@fortawesome/pro-duotone-svg-icons';
import { InteractionStatus } from '@azure/msal-browser';

const LoginMSALAppSuspense = lazy(() => import('./LoginMSALAppSuspense'));

function LoginMSALConnector() {
  const {
    userLoggedIn,
    isLoadingToken,
    calledInit,
    loginError,
    needOtpCode,
    noSessionAvailable,
    callFOCUSInit,
    needToChoosePDV,
    refreshToken,
    inProgress,
  } = useLoginMsal();

  const userLoggedButNotInit =
    userLoggedIn &&
    !calledInit &&
    !needOtpCode &&
    !needToChoosePDV &&
    !noSessionAvailable;
  // Guard difensivo: non mostrare LoginPage se MSAL sta ancora processando un redirect
  const userNotLoggedIn =
    !userLoggedIn && !isLoadingToken && inProgress === InteractionStatus.None;
  const needLoader = isLoadingToken || userLoggedButNotInit;

  // Debug logging per tracciare lo stato del connector
  useEffect(() => {
    console.log('[MSAL Connector] State:', {
      userLoggedIn,
      isLoadingToken,
      calledInit,
      loginError,
      needOtpCode,
      needToChoosePDV,
      noSessionAvailable,
      inProgress,
      computed: {
        userLoggedButNotInit,
        userNotLoggedIn,
        needLoader,
      },
    });
  }, [
    userLoggedIn,
    isLoadingToken,
    calledInit,
    loginError,
    needOtpCode,
    needToChoosePDV,
    noSessionAvailable,
    inProgress,
    userLoggedButNotInit,
    userNotLoggedIn,
    needLoader,
  ]);

  if (needLoader) {
    return <LoginBlankLoaderPage />;
  }

  if (loginError) {
    return <LoginMSALInitError />;
  }

  if (userNotLoggedIn) {
    return <LoginPage />;
  }
  if (noSessionAvailable) {
    return (
      <LoginChooseSessionPage
        onSessionSet={async (sessionRef: string | undefined) => {
          await callFOCUSInit({}, sessionRef);

          return;
        }}
      />
    );
  }

  if (needToChoosePDV) {
    return (
      <LoginChoosePdvPage
        onPdvSet={async (workplaceRef: string) => {
          await refreshToken(workplaceRef);
        }}
      />
    );
  }

  if (needOtpCode) {
    return (
      <LoginOTPRequestPage
        onOtpInserted={(otp, remind) => {
          callFOCUSInit({ otpCode: otp, otpRemember: remind }, undefined);
        }}
      />
    );
  }

  // Fallback difensivo: se nessuna guardia ha catturato lo stato ma l'utente
  // non è autenticato o l'init non è completato, mostrare il loader.
  // Copre il gap durante logout (inProgress=Logout, userLoggedIn=false)
  // e durante redirect MSAL (inProgress=HandleRedirect).
  if (!userLoggedIn || !calledInit) {
    return <LoginBlankLoaderPage />;
  }

  return (
    <Suspense fallback={<LoginBlankLoaderPage />}>
      <LoginMSALAppSuspense />
    </Suspense>
  );
}

export default LoginMSALConnector;
